import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Lightbulb, TrendUp, TrendDown, Warning, ArrowRight, ArrowsClockwise } from "phosphor-react";
import { Button } from "@/components/ui/button";

// Import the same constants and helper from Dashboard
const INSIGHTS_MIN_TRADES = 10;
const MIN_SAMPLE_SIZE = 5;
const MIN_DIFF_THRESHOLD = 15;
const MAX_IMBALANCE_RATIO = 4;

/**
 * Generate trading insights from trade data.
 * This is the same function used in Dashboard.js to ensure consistent ordering.
 */
function generateInsights(trades, setups = []) {
  if (!trades || !Array.isArray(trades)) return { insights: [], allInsights: [], hasLowQualityData: false };
  
  // Build setup name lookup map
  const setupNameMap = {};
  if (setups && Array.isArray(setups)) {
    setups.forEach(s => {
      if (s.id && s.name) {
        setupNameMap[s.id] = s.name;
      }
    });
  }
  
  // Filter to only closed trades with valid data
  const validTrades = trades.filter(t => 
    t.status === 'closed' && 
    t.outcome && 
    ['win', 'loss', 'breakeven'].includes(t.outcome)
  );
  
  // Get canceled/not-triggered trades for planned trades behavior insight
  const canceledTrades = trades.filter(t => t.status === 'canceled');
  
  if (validTrades.length < INSIGHTS_MIN_TRADES) {
    return { insights: [], allInsights: [], hasLowQualityData: false };
  }
  
  const insights = [];
  
  // Helper: Calculate win rate for a group of trades
  const calcWinRate = (group) => {
    if (!group || group.length === 0) return 0;
    const wins = group.filter(t => t.outcome === 'win').length;
    return (wins / group.length) * 100;
  };
  
  // Helper: Calculate average RR for a group
  const calcAvgRR = (group) => {
    if (!group || group.length === 0) return 0;
    const withRR = group.filter(t => t.rr != null && !isNaN(t.rr));
    if (withRR.length === 0) return 0;
    return withRR.reduce((sum, t) => sum + t.rr, 0) / withRR.length;
  };
  
  // Helper: Check if comparison is statistically balanced
  const isBalancedComparison = (groupA, groupB) => {
    if (!groupA || !groupB) return false;
    if (groupA.length < MIN_SAMPLE_SIZE || groupB.length < MIN_SAMPLE_SIZE) return false;
    const ratio = Math.max(groupA.length, groupB.length) / Math.min(groupA.length, groupB.length);
    return ratio <= MAX_IMBALANCE_RATIO;
  };
  
  // Helper: Check if win rate difference is meaningful
  const isMeaningfulDifference = (rateA, rateB, groupASize, groupBSize, threshold = MIN_DIFF_THRESHOLD) => {
    const diff = Math.abs(rateA - rateB);
    if (diff < threshold) return false;
    const minSize = Math.min(groupASize, groupBSize);
    if (minSize < 8 && (rateA === 100 || rateA === 0 || rateB === 100 || rateB === 0)) {
      return false;
    }
    return true;
  };
  
  // Sort trades by close date (most recent first)
  const sortedTrades = [...validTrades].sort((a, b) => 
    new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at)
  );
  
  // ============================================
  // INSIGHT 1: RECENT RR MOMENTUM
  // ============================================
  if (sortedTrades.length >= 20) {
    const recentTrades = sortedTrades.slice(0, 10);
    const previousTrades = sortedTrades.slice(10, 20);
    
    const avgRR_recent = calcAvgRR(recentTrades);
    const avgRR_previous = calcAvgRR(previousTrades);
    
    if (Math.abs(avgRR_previous) > 0.1) {
      const deltaPercent = ((avgRR_recent - avgRR_previous) / Math.abs(avgRR_previous)) * 100;
      
      if (Math.abs(deltaPercent) >= 25) {
        if (deltaPercent > 0) {
          insights.push({
            type: 'positive',
            text: `Your recent RR is improving`,
            subtext: `Your last 10 trades show stronger RR consistency`
          });
        } else {
          insights.push({
            type: 'warning',
            text: `Your recent RR is slipping`,
            subtext: `Review the quality of your recent setups to stay consistent`,
            link: {
              path: '/history',
              params: { view: 'recent-rr' }
            }
          });
        }
      }
    }
  }
  
  // ============================================
  // INSIGHT 2: PLANNED TRADES BEHAVIOR
  // ============================================
  const plannedTrades = trades.filter(t => t.is_planned === true);
  const totalPlannedTrades = plannedTrades.length;
  
  if (totalPlannedTrades >= 10) {
    const notTriggeredPlanned = plannedTrades.filter(t => 
      t.status === 'canceled' && 
      t.cancel_reason && 
      ['setup_not_triggered', 'no_entry_reach'].includes(t.cancel_reason)
    );
    const notTriggeredRate = (notTriggeredPlanned.length / totalPlannedTrades) * 100;
    
    const skippedExecutions = plannedTrades.filter(t => 
      t.status === 'canceled' && 
      t.cancel_reason === 'skipped_execution'
    );
    const skippedExecutionRate = (skippedExecutions.length / totalPlannedTrades) * 100;
    
    let addedPlannedInsight = false;
    
    if (skippedExecutionRate >= 30 && !addedPlannedInsight) {
      insights.push({
        type: 'warning',
        text: `You're skipping executions frequently`,
        subtext: `Executing your plan more consistently may improve discipline`
      });
      addedPlannedInsight = true;
    }
    
    if (notTriggeredRate >= 50 && !addedPlannedInsight) {
      insights.push({
        type: 'warning',
        text: `Many of your planned setups never reach execution`,
        subtext: `Review your entry levels or market selection`
      });
      addedPlannedInsight = true;
    }
    
    if (!addedPlannedInsight && 
        notTriggeredRate < 25 && 
        skippedExecutionRate < 15 && 
        totalPlannedTrades >= 20) {
      insights.push({
        type: 'positive',
        text: `You're executing your planned setups consistently`,
        subtext: `Most of your planned trades are being followed through`
      });
    }
  }
  
  // ============================================
  // INSIGHT 3: CONFIDENCE PERFORMANCE
  // ============================================
  const tradesWithConfidence = validTrades.filter(t => t.confidence);
  const lowConfTrades = tradesWithConfidence.filter(t => t.confidence === 'low');
  const highConfTrades = tradesWithConfidence.filter(t => t.confidence === 'high');
  
  if (tradesWithConfidence.length >= 5 && isBalancedComparison(lowConfTrades, highConfTrades)) {
    const lowConfWinRate = calcWinRate(lowConfTrades);
    const highConfWinRate = calcWinRate(highConfTrades);
    
    if (isMeaningfulDifference(lowConfWinRate, highConfWinRate, lowConfTrades.length, highConfTrades.length)) {
      if (lowConfWinRate < highConfWinRate) {
        insights.push({
          type: 'warning',
          text: `Low confidence trades are hurting your performance`
        });
      } else {
        insights.push({
          type: 'positive',
          text: `Your low confidence reads are actually solid — trust your analysis`
        });
      }
    }
  }
  
  // ============================================
  // INSIGHT 4: SETUP PERFORMANCE
  // ============================================
  if (validTrades.length >= 10) {
    const setupGroups = {};
    validTrades.forEach(t => {
      const setupId = t.setup_id || '__no_setup__';
      // Use setupNameMap to get the correct name (more reliable than t.setup_name)
      const setupName = setupId !== '__no_setup__' 
        ? (setupNameMap[setupId] || t.setup_name || 'Unknown Setup')
        : 'No setup';
      if (!setupGroups[setupId]) {
        setupGroups[setupId] = {
          id: setupId,
          name: setupName,
          trades: []
        };
      }
      setupGroups[setupId].trades.push(t);
    });
    
    const calcProfitFactor = (trades) => {
      const wins = trades.filter(t => t.outcome === 'win');
      const losses = trades.filter(t => t.outcome === 'loss');
      const totalWinRR = wins.reduce((sum, t) => sum + (t.rr || 0), 0);
      const totalLossRR = losses.reduce((sum, t) => sum + Math.abs(t.rr || 0), 0);
      if (totalLossRR === 0) return totalWinRR > 0 ? Infinity : 0;
      return totalWinRR / totalLossRR;
    };
    
    const setupAnalytics = Object.values(setupGroups)
      .filter(group => group.id !== '__no_setup__')
      .map(group => ({
        id: group.id,
        name: group.name,
        trades: group.trades.length,
        winRate: calcWinRate(group.trades),
        avgRR: calcAvgRR(group.trades),
        profitFactor: calcProfitFactor(group.trades)
      }));
    
    const addedSetupInsights = new Set();
    
    // Most used but underperforming
    if (setupAnalytics.length > 0) {
      const mostUsed = [...setupAnalytics].sort((a, b) => b.trades - a.trades)[0];
      if (mostUsed.trades >= MIN_SAMPLE_SIZE && mostUsed.profitFactor < 1.2 && mostUsed.profitFactor !== Infinity) {
        insights.push({
          type: 'warning',
          text: `You're frequently trading ${mostUsed.name}, but performance is below optimal`,
          subtext: `Consider reviewing your entries or reducing exposure to this setup`,
          link: {
            path: `/analytics/setup/${mostUsed.id}`
          }
        });
        addedSetupInsights.add('most_used_underperform');
      }
    }
    
    // Top performing setup
    const qualifiedForTop = setupAnalytics.filter(s => s.trades >= MIN_SAMPLE_SIZE && s.profitFactor !== Infinity);
    if (qualifiedForTop.length > 0) {
      const topPerforming = [...qualifiedForTop].sort((a, b) => b.profitFactor - a.profitFactor)[0];
      if (topPerforming.profitFactor >= 1.5) {
        insights.push({
          type: 'positive',
          text: `${topPerforming.name} is currently your best performing setup`,
          subtext: `Consider focusing more on this pattern`
        });
        addedSetupInsights.add('top_performing');
      }
    }
    
    // Needs review setup
    if (qualifiedForTop.length > 0) {
      const needsReview = [...qualifiedForTop].sort((a, b) => a.profitFactor - b.profitFactor)[0];
      if (needsReview.profitFactor < 1.0) {
        insights.push({
          type: 'warning',
          text: `${needsReview.name} is underperforming`,
          subtext: `This setup may need adjustments or should be avoided`,
          link: {
            path: `/analytics/setup/${needsReview.id}`
          }
        });
        addedSetupInsights.add('needs_review');
      }
    }
    
    // High consistency setup
    const reliableSetups = setupAnalytics.filter(s => 
      s.trades >= MIN_SAMPLE_SIZE && 
      s.profitFactor >= 1.5 && 
      s.profitFactor !== Infinity
    );
    if (reliableSetups.length > 0 && !addedSetupInsights.has('top_performing')) {
      const mostReliable = [...reliableSetups].sort((a, b) => b.profitFactor - a.profitFactor)[0];
      insights.push({
        type: 'positive',
        text: `${mostReliable.name} is one of your most reliable setups`,
        subtext: `Its performance is consistently strong across your recent trades`
      });
      addedSetupInsights.add('high_consistency');
    }
  }
  
  // ============================================
  // INSIGHT 5: NO SETUP TRADES
  // ============================================
  const noSetupTrades = validTrades.filter(t => !t.setup_name || t.setup_name === 'No setup');
  const withSetupTrades = validTrades.filter(t => t.setup_name && t.setup_name !== 'No setup');
  
  if (isBalancedComparison(noSetupTrades, withSetupTrades)) {
    const noSetupWinRate = calcWinRate(noSetupTrades);
    const withSetupWinRate = calcWinRate(withSetupTrades);
    
    if (isMeaningfulDifference(noSetupWinRate, withSetupWinRate, noSetupTrades.length, withSetupTrades.length)) {
      if (noSetupWinRate < withSetupWinRate) {
        insights.push({
          type: 'warning',
          text: `Trades without a setup are reducing your consistency`
        });
      }
    }
  }
  
  // ============================================
  // INSIGHT 6: TIMEFRAME PERFORMANCE
  // ============================================
  const timeframeGroups = {};
  validTrades.forEach(t => {
    const tf = t.timeframe || 'Unknown';
    if (tf !== 'Unknown') {
      if (!timeframeGroups[tf]) timeframeGroups[tf] = [];
      timeframeGroups[tf].push(t);
    }
  });
  
  const timeframeAnalytics = Object.entries(timeframeGroups)
    .filter(([_, trades]) => trades.length >= MIN_SAMPLE_SIZE)
    .map(([timeframe, trades]) => ({
      timeframe,
      trades: trades.length,
      winRate: calcWinRate(trades),
      avgRR: calcAvgRR(trades)
    }));
  
  if (timeframeAnalytics.length > 1) {
    const sortedByWinRate = [...timeframeAnalytics].sort((a, b) => b.winRate - a.winRate);
    const best = sortedByWinRate[0];
    const worst = sortedByWinRate[sortedByWinRate.length - 1];
    
    if (isMeaningfulDifference(best.winRate, worst.winRate, best.trades, worst.trades)) {
      insights.push({
        type: 'positive',
        text: `${best.timeframe} is your most consistent timeframe`,
        subtext: `${best.winRate.toFixed(0)}% win rate across ${best.trades} trades`
      });
    }
  }
  
  // ============================================
  // INSIGHT 7: PARTIAL EXECUTION
  // ============================================
  const setupTradeGroups = {};
  validTrades.forEach(t => {
    const setupId = t.setup_id || '__no_setup__';
    // Use setupNameMap to get the correct name (more reliable than t.setup_name)
    const setupName = setupId !== '__no_setup__' 
      ? (setupNameMap[setupId] || t.setup_name || 'Unknown Setup')
      : 'No setup';
    if (!setupTradeGroups[setupId]) {
      setupTradeGroups[setupId] = { name: setupName, trades: [] };
    }
    setupTradeGroups[setupId].trades.push(t);
  });
  
  let worstPartialSetup = null;
  let worstPartialSetupId = null;
  let worstPartialMetrics = null;
  
  Object.entries(setupTradeGroups).forEach(([setupKey, group]) => {
    const setupTrades = group.trades.filter(t => t.hand_size > 0);
    const withPartial = setupTrades.filter(t => t.partial_used === true);
    const withoutPartial = setupTrades.filter(t => !t.partial_used);
    
    if (withPartial.length >= 3 && withoutPartial.length >= 3) {
      const avgPartialR = withPartial.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / withPartial.length;
      const avgNoPartialR = withoutPartial.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / withoutPartial.length;
      const deltaR = avgPartialR - avgNoPartialR;
      
      const partialWithRR = withPartial.filter(t => 
        t.first_partial_rr !== null && t.first_partial_rr !== undefined
      );
      
      let avgGivenBack = 0;
      if (partialWithRR.length >= 3) {
        avgGivenBack = partialWithRR.reduce((sum, t) => {
          const finalR = t.profit_usd / t.hand_size;
          return sum + Math.max(0, t.first_partial_rr - finalR);
        }, 0) / partialWithRR.length;
      }
      
      const hasDeltaProblem = deltaR <= -0.25;
      const hasGiveBackProblem = avgGivenBack >= 0.5;
      
      if (hasDeltaProblem || hasGiveBackProblem) {
        const severity = Math.abs(deltaR) + avgGivenBack;
        if (!worstPartialMetrics || severity > worstPartialMetrics.severity) {
          worstPartialSetup = group.name;
          worstPartialSetupId = setupKey;
          worstPartialMetrics = {
            deltaR,
            avgGivenBack,
            avgPartialR,
            avgNoPartialR,
            severity,
            hasDeltaProblem,
            hasGiveBackProblem
          };
        }
      }
    }
  });
  
  if (worstPartialSetup && worstPartialMetrics) {
    let insightText, insightSubtext;
    
    if (worstPartialSetup !== 'No setup') {
      insightText = `Partial execution is hurting your edge on ${worstPartialSetup}`;
      if (worstPartialMetrics.hasGiveBackProblem) {
        insightSubtext = `You're giving back ${worstPartialMetrics.avgGivenBack.toFixed(2)}R on average after first partial.`;
      } else {
        insightSubtext = `Trades without partial outperform trades with partial by ${Math.abs(worstPartialMetrics.deltaR).toFixed(2)}R.`;
      }
    } else {
      insightText = `Partial execution is hurting your performance`;
      insightSubtext = `Trades without partial are outperforming trades with partial.`;
    }
    
    insights.push({
      type: 'warning',
      text: insightText,
      subtext: insightSubtext,
      _priority: 1,
      link: {
        path: '/advanced/partial-execution',
        params: worstPartialSetupId ? { setup: worstPartialSetupId } : null
      }
    });
  }
  
  // ============================================
  // PLANNED TRADES NOT TRIGGERED INSIGHT
  // ============================================
  const totalNotTriggered = canceledTrades.length;
  const totalTradesCount = trades.length;
  
  const notTriggeredRate = totalTradesCount > 0 ? totalNotTriggered / totalTradesCount : 0;
  const hasEnoughPlannedData = totalTradesCount >= 15 || totalNotTriggered >= 10;
  
  if (hasEnoughPlannedData && notTriggeredRate >= 0.30 && totalNotTriggered > 0) {
    const plannedCounts = {
      setup_not_triggered: 0,
      no_entry_reach: 0,
      skipped_execution: 0
    };
    
    canceledTrades.forEach(t => {
      const reason = t.cancel_reason;
      if (reason && plannedCounts.hasOwnProperty(reason)) {
        plannedCounts[reason]++;
      }
    });
    
    const sortedReasons = Object.entries(plannedCounts)
      .sort((a, b) => b[1] - a[1])
      .filter(([_, count]) => count > 0);
    
    let dominantCause = 'skipped';
    if (sortedReasons.length > 0) {
      const [topReason] = sortedReasons[0];
      if (topReason === 'no_entry_reach') dominantCause = 'entry';
      else if (topReason === 'setup_not_triggered') dominantCause = 'setup';
      else dominantCause = 'skipped';
    }
    
    const setupCounts = {};
    canceledTrades.forEach(t => {
      const setupName = t.setup_name || 'No setup';
      if (!setupCounts[setupName]) setupCounts[setupName] = 0;
      setupCounts[setupName]++;
    });
    
    let worstSetup = null;
    const sortedSetups = Object.entries(setupCounts)
      .filter(([name]) => name !== 'No setup')
      .sort((a, b) => b[1] - a[1]);
    
    if (sortedSetups.length > 0 && sortedSetups[0][1] >= 3) {
      worstSetup = sortedSetups[0][0];
    }
    
    let plannedText, plannedSubtext;
    
    if (dominantCause === 'entry') {
      plannedText = worstSetup 
        ? `${worstSetup} setups are often not reaching entry`
        : "Many of your setups are not reaching entry";
      plannedSubtext = "Your entry levels may be too far from price action";
    } else if (dominantCause === 'setup') {
      plannedText = worstSetup
        ? `${worstSetup} setups often fail before triggering`
        : "Your setups often fail before triggering";
      plannedSubtext = "You may need to refine your setup conditions";
    } else {
      plannedText = worstSetup
        ? `You're skipping many ${worstSetup} planned trades`
        : "You're skipping many planned trades";
      plannedSubtext = "This may indicate hesitation or lack of discipline";
    }
    
    insights.push({
      type: 'warning',
      text: plannedText,
      subtext: plannedSubtext,
      _priority: 2,
      link: {
        path: '/advanced/planned-trades',
        params: null
      }
    });
  }
  
  // ============================================
  // WINNING RR DEGRADATION
  // ============================================
  if (validTrades.length >= 15) {
    const tradesWithRR = validTrades.filter(t => 
      t.hand_size > 0 && 
      t.profit_usd !== null && 
      t.profit_usd !== undefined
    );
    
    const winningTrades = tradesWithRR.filter(t => t.outcome === 'win');
    
    if (winningTrades.length >= 5) {
      const avgWinRR = winningTrades.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / winningTrades.length;
      
      const sortedWinsByDate = [...winningTrades].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      let recentWinRR = null;
      let previousWinRR = null;
      let isWinRRTrending = false;
      
      if (sortedWinsByDate.length >= 10) {
        const recent5Wins = sortedWinsByDate.slice(0, 5);
        const previous5Wins = sortedWinsByDate.slice(5, 10);
        
        recentWinRR = recent5Wins.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / 5;
        previousWinRR = previous5Wins.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / 5;
        
        isWinRRTrending = previousWinRR >= 1.0 && recentWinRR < previousWinRR && (previousWinRR - recentWinRR) >= 0.3;
      }
      
      const isWinRRLow = avgWinRR < 1.2;
      
      if (isWinRRTrending || isWinRRLow) {
        let rrText, rrSubtext;
        
        if (isWinRRTrending) {
          rrText = "Your recent winning trades have lower RR";
          rrSubtext = `Recent wins: ${recentWinRR.toFixed(2)}R vs Previous: ${previousWinRR.toFixed(2)}R`;
        } else if (isWinRRLow) {
          rrText = "Your winning trades are returning less RR";
          rrSubtext = `Avg win RR: ${avgWinRR.toFixed(2)}R — consider reviewing your exit strategy`;
        }
        
        insights.push({
          type: 'warning',
          text: rrText,
          subtext: rrSubtext,
          _priority: 3,
          // Navigation to History with winning-rr view
          link: {
            path: '/history',
            params: { 
              view: 'winning-rr',
              condition: isWinRRTrending ? 'trend' : 'static'
            }
          }
        });
      }
    }
  }
  
  // ============================================
  // TRADING STREAK
  // ============================================
  const calculateTradingStreak = () => {
    const closedTrades = validTrades.filter(t => 
      t.profit_usd !== null && t.profit_usd !== undefined
    );
    
    if (closedTrades.length < 3) return null;
    
    const tradesByDay = {};
    closedTrades.forEach(trade => {
      if (!trade.created_at) return;
      const dayKey = new Date(trade.created_at).toISOString().split('T')[0];
      if (!tradesByDay[dayKey]) {
        tradesByDay[dayKey] = [];
      }
      tradesByDay[dayKey].push(trade);
    });
    
    const activeDays = Object.entries(tradesByDay)
      .map(([date, dayTrades]) => {
        const totalPnL = dayTrades.reduce((sum, t) => sum + (t.profit_usd || 0), 0);
        return { date, totalPnL };
      })
      .filter(day => day.totalPnL !== 0)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    if (activeDays.length < 3) return null;
    
    const sequence = activeDays.map(day => day.totalPnL > 0 ? 'win' : 'loss');
    
    const streakType = sequence[0];
    let streakLength = 1;
    
    for (let i = 1; i < sequence.length; i++) {
      if (sequence[i] === streakType) {
        streakLength++;
      } else {
        break;
      }
    }
    
    return { type: streakType, length: streakLength };
  };
  
  const streak = calculateTradingStreak();
  
  if (streak) {
    if (streak.type === 'loss' && streak.length >= 3) {
      insights.push({
        type: 'warning',
        text: `You're on a ${streak.length}-day losing streak`,
        subtext: 'Consider slowing down and reviewing your recent trades',
        _priority: 4
      });
    } else if (streak.type === 'win' && streak.length >= 3) {
      insights.push({
        type: 'positive',
        text: `You're on a ${streak.length}-day winning streak`,
        subtext: 'Keep following what is working in your recent trades',
        _priority: 10
      });
    }
  }
  
  // ============================================
  // SORT AND CLEAN
  // ============================================
  const sortedInsights = [...insights].sort((a, b) => {
    const aPriority = a._priority || 99;
    const bPriority = b._priority || 99;
    return aPriority - bPriority;
  });
  
  const cleanedInsights = sortedInsights.map(({ _priority, ...rest }) => rest);
  
  return { 
    insights: cleanedInsights.slice(0, 3),
    allInsights: cleanedInsights,
    hasLowQualityData: false 
  };
}

export default function AllInsights() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Try to get insights from navigation state first (for instant display)
  const passedInsights = location.state?.allInsights;
  
  const [trades, setTrades] = useState([]);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(!passedInsights);
  
  useEffect(() => {
    // If we have passed insights, no need to fetch
    if (passedInsights) return;
    
    fetchData();
  }, [passedInsights]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [tradesRes, setupsRes] = await Promise.all([
        api.get('/trades'),
        api.get('/setups')
      ]);
      setTrades(tradesRes.data || []);
      setSetups(setupsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Generate insights (only if we don't have passed insights)
  const allInsights = useMemo(() => {
    if (passedInsights) return passedInsights;
    if (loading || trades.length === 0) return [];
    
    const result = generateInsights(trades, setups);
    return result.allInsights || [];
  }, [passedInsights, trades, setups, loading]);
  
  const handleInsightClick = (insight) => {
    if (!insight.link || !insight.link.path) return;
    
    const { path, params } = insight.link;
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      navigate(`${path}?${queryString}`);
    } else {
      navigate(path);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Header */}
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 pt-8">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-white/60 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-[#00E676]/15 to-[#00E676]/5 rounded-sm flex items-center justify-center border border-[#00E676]/15">
            <Lightbulb size={24} weight="duotone" className="text-[#00E676]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading text-white">All Insights</h1>
            <p className="text-white/50 text-sm mt-0.5">
              {allInsights.length} insight{allInsights.length !== 1 ? 's' : ''} based on your trading data
            </p>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 pb-12">
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-12 text-white/40">
            <ArrowsClockwise size={20} className="animate-spin" />
            <span>Analyzing your trades...</span>
          </div>
        ) : allInsights.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
            <Lightbulb size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/60 text-lg font-medium">No insights available yet</p>
            <p className="text-white/40 text-sm mt-2">
              Keep logging trades to unlock personalized insights
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {allInsights.map((insight, idx) => {
              const isClickable = insight.link && insight.link.path;
              
              return (
                <div
                  key={idx}
                  onClick={() => isClickable && handleInsightClick(insight)}
                  className={`group flex items-start gap-4 p-4 rounded-sm border transition-all duration-200 ${
                    insight.type === 'positive' 
                      ? 'bg-[#00E676]/5 border-[#00E676]/10' 
                      : insight.type === 'warning'
                      ? 'bg-[#FFC107]/5 border-[#FFC107]/10'
                      : 'bg-white/[0.02] border-white/10'
                  } ${isClickable ? 'cursor-pointer hover:border-white/30 hover:bg-white/[0.03]' : ''}`}
                  data-testid={`insight-${idx}`}
                >
                  <div className={`w-10 h-10 rounded flex items-center justify-center shrink-0 ${
                    insight.type === 'positive' 
                      ? 'bg-[#00E676]/15' 
                      : insight.type === 'warning'
                      ? 'bg-[#FFC107]/15'
                      : 'bg-white/10'
                  }`}>
                    {insight.type === 'positive' ? (
                      <TrendUp size={20} weight="bold" className="text-[#00E676]" />
                    ) : insight.type === 'warning' ? (
                      <Warning size={20} weight="bold" className="text-[#FFC107]" />
                    ) : (
                      <Lightbulb size={20} weight="bold" className="text-white/60" />
                    )}
                  </div>
                  <div className="flex-1 py-1">
                    <p className="text-base text-white/90 leading-snug font-medium">
                      {insight.text}
                    </p>
                    {insight.subtext && (
                      <p className="text-sm text-white/50 mt-1.5 leading-snug">
                        {insight.subtext}
                      </p>
                    )}
                    {insight.bullets && insight.bullets.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {insight.bullets.map((bullet, bIdx) => (
                          <li key={bIdx} className="text-sm text-white/40 flex items-start gap-2">
                            <span className="text-white/30 mt-0.5">•</span>
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* Arrow indicator for clickable insights */}
                  {isClickable && (
                    <div className="shrink-0 self-center opacity-40 group-hover:opacity-80 transition-opacity">
                      <ArrowRight size={18} weight="bold" className="text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
